/**
 * src/types/charts/bar-horizontal.chart.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Horizontal bar chart — categories on y-axis, values on x-axis.
 * Ideal for ranked lists and comparisons with long category names.
 */

import type { ChartDataPoint } from '../core/interfaces.shared';
import {
  makeTitle, makeSubtitle,
  DEFAULT_BACKGROUND, DEFAULT_AXES, DEFAULT_LEGEND, DEFAULT_DATA_LABELS,
} from '../core/defaults.shared';
import type { ChartConfigBase } from '../core/chart-config.base';
import type { SchemaNote } from '../chart.schema';

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION STYLES
// ─────────────────────────────────────────────────────────────────────────────

export type BarHorizontalAnimationStyle =
  | 'classic' | 'sequential' | 'rankReveal' | 'slideIn' | 'sweep'
  | 'elastic' | 'wave' | 'fadeScale' | 'popIn' | 'magnitudeStagger';

export const BAR_HORIZONTAL_ANIMATION_STYLES: BarHorizontalAnimationStyle[] = [
  'classic', 'sequential', 'rankReveal', 'slideIn', 'sweep',
  'elastic', 'wave', 'fadeScale', 'popIn', 'magnitudeStagger',
];

export const BAR_HORIZONTAL_ELEMENT_PANEL_STYLES: BarHorizontalAnimationStyle[] =
  BAR_HORIZONTAL_ANIMATION_STYLES;


// ─────────────────────────────────────────────────────────────────────────────
// DATA POINT
// ─────────────────────────────────────────────────────────────────────────────

export interface BarHorizontalDataPoint extends ChartDataPoint {
  chartType:     'bar-horizontal';
  value:         number;    // bar length. Must be >= 0.
  group?:        string;
  showValue?:    boolean;   // show value label at bar end. Default: true.
  labelOffsetX?: number;
  labelOffsetY?: number;
}


// ─────────────────────────────────────────────────────────────────────────────
// SPACING CONFIG
// ─────────────────────────────────────────────────────────────────────────────

export interface BarHorizontalSpacingConfig {
  chartType:    'bar-horizontal';
  barHeight:    number;    // thickness of each bar in px. Range: 10–300. Default: 60.
  barSpacing:   number;    // gap between bars within a group. Range: 0–200. Default: 30.
}


// ─────────────────────────────────────────────────────────────────────────────
// DEFAULTS
// ─────────────────────────────────────────────────────────────────────────────

export const BAR_HORIZONTAL_DEFAULTS: ChartConfigBase<BarHorizontalSpacingConfig> = {
  chartType: 'bar-horizontal',
  content: {
    titles:    [makeTitle('Performance Ranking')],
    subtitles: [makeSubtitle('Top Categories by Value')],
    infoBoxes: [],
  },
  animation:  { duration: 5, style: 'classic', textSpeed: 1 },
  background: { ...DEFAULT_BACKGROUND },
  layout: {
    chartPosition: { x: 0, y: 0 },
    // Wider left margin — category labels live in left margin
    chartMargin: { top: 180, right: 120, bottom: 100, left: 220 },
  },
  axes:       { ...DEFAULT_AXES },
  legend:     { ...DEFAULT_LEGEND },
  dataLabels: { ...DEFAULT_DATA_LABELS },
  spacing: {
    chartType:    'bar-horizontal',
    barHeight:    60,
    barSpacing:   30,
  },
  export: { quality: '1080p', isLooping: true, canvasFormat: '16:9' },
};


// ─────────────────────────────────────────────────────────────────────────────
// SCHEMA NOTES — consumed by chart.schema.ts merger and getChartSchemaSlice()
// ─────────────────────────────────────────────────────────────────────────────

export const BAR_HORIZONTAL_SCHEMA_NOTES: Record<string, SchemaNote> = {

  'bar-horizontal.barHeight': {
    description: 'Thickness of each horizontal bar in canvas pixels (vertical dimension).',
    type:    'number',
    range:   '10–300',
    default: 60,
    unit:    'canvas pixels',
    affects: ['bar visual thickness', 'total chart height'],
    bounds:  ['Min 10. Max 300.'],
    synergizes: ['bar-horizontal.barSpacing — together control density of the ranked list'],
    tensions:   ['data count — thick bars with many data points overflow chart height'],
    agentNote:  "Default 60px fits ~10 bars comfortably at 1080px height. For 15+ bars reduce to 30–40px. For 5 or fewer bars increase to 80–100px for visual impact.",
  },

  'bar-horizontal.barSpacing': {
    description: 'Gap between horizontal bars in canvas pixels.',
    type:    'number',
    range:   '0–200',
    default: 30,
    unit:    'canvas pixels',
    affects: ['inter-bar gap'],
    bounds:  ['Min 0. Max 200.'],
    synergizes: ['bar-horizontal.barHeight'],
    tensions:   [],
    agentNote:  "Controls readability of ranked lists. 20–40px is standard. Tighter (10–15px) for dense data. Wider (50+) for dramatic emphasis.",
  },

  'dataPoint.bar-horizontal.value': {
    description: 'Numeric value determining bar length. Bar extends rightward proportional to value / maxValue.',
    type:    'number',
    range:   'any positive number; zero is valid',
    default: null,
    unit:    'arbitrary numeric unit',
    affects: ['bar length', 'value axis tick range', 'value label at bar tip'],
    bounds:  ['Must be >= 0. Negative values not supported.'],
    synergizes: ['dataLabels.style.fontSize — value labels are at bar tips; scale down for short bars'],
    tensions:   ['max/min ratio — large outliers make small bars nearly invisible'],
    agentNote:  "Bars extend from left (value axis baseline) to right. Data should be sorted by value (descending) for ranked lists — the topmost bar is conventionally the largest. The renderer does NOT auto-sort.",
  },

};
